import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, STATUS_BADGE, STATUS_LABEL } from "@/lib/invoicing";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, Loader2, RefreshCw, Pencil, Trash2, Play, BadgeDollarSign,
} from "lucide-react";

const SUMMARY_CARDS = [
  ["outstanding", "Outstanding", "text-blue-700"],
  ["overdue", "Overdue", "text-red-600"],
  ["drafts", "Drafts", "text-zinc-600"],
  ["paid_this_month", "Paid this month", "text-emerald-700"],
];

const F = ({ label, children }) => (
  <div><div className="text-xs font-medium text-zinc-500 mb-1">{label}</div>{children}</div>
);

function InvoicesTab() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (search) params.set("search", search);
      const [s, l] = await Promise.all([
        api.get("/invoicing/summary"),
        api.get(`/invoicing/invoices?${params}`),
      ]);
      setSummary(s.data); setRows(l.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally { setLoading(false); }
  }, [status, search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {SUMMARY_CARDS.map(([k, label, cls]) => (
          <Card key={k} data-testid={`invoicing-summary-${k}`}>
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500">{label}</div>
              <div className={`text-xl font-bold ${cls}`}>
                {fmtMoney(summary?.[k]?.amount)}
              </div>
              <div className="text-xs text-zinc-400">{summary?.[k]?.count ?? 0} invoice(s)</div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search number / client / reference…" value={search}
          onChange={(e) => setSearch(e.target.value)} className="w-64"
          data-testid="invoice-search-input" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="invoice-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABEL).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={load} data-testid="invoice-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <div className="flex-1" />
        <Button onClick={() => nav("/invoicing/invoices/new")} data-testid="new-invoice-btn">
          <Plus className="h-4 w-4 mr-1" /> New invoice
        </Button>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
                <th className="p-3">Number</th><th className="p-3">Client</th>
                <th className="p-3">Issued</th><th className="p-3">Due</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Balance</th>
                <th className="p-3">Status</th><th className="p-3">Xero</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.invoice_id}
                  className="border-b hover:bg-blue-50/40 cursor-pointer"
                  data-testid={`invoice-row-${inv.invoice_number}`}
                  onClick={() => nav(`/invoicing/invoices/${inv.invoice_id}`)}>
                  <td className="p-3 font-medium text-[#002FA7]">{inv.invoice_number}</td>
                  <td className="p-3">{inv.client_name}</td>
                  <td className="p-3">{inv.issue_date}</td>
                  <td className="p-3">{inv.due_date}</td>
                  <td className="p-3 text-right">{fmtMoney(inv.total)}</td>
                  <td className="p-3 text-right font-medium">{fmtMoney(inv.balance_due)}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={STATUS_BADGE[inv.status]}>
                      {STATUS_LABEL[inv.status]}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {inv.xero_pushed
                      ? <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">In Xero</Badge>
                      : <span className="text-zinc-400 text-xs">—</span>}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={8} className="p-8 text-center text-zinc-400">
                  No invoices yet — create your first one.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function RecurringTab() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invoicing/recurring");
      setRows(data);
    } catch (e) { toast.error("Failed to load recurring invoices"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (rec) => {
    try {
      await api.put(`/invoicing/recurring/${rec.recurring_id}`, {
        ...rec, active: !rec.active,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const runNow = async (rec) => {
    setRunning(rec.recurring_id);
    try {
      const { data } = await api.post(
        `/invoicing/recurring/${rec.recurring_id}/run-now`,
        { origin_url: window.location.origin });
      toast.success(`Generated ${data.invoice_number}${data.auto_sent ? " and emailed" : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setRunning(null); }
  };

  const del = async (rec) => {
    if (!window.confirm(`Delete recurring invoice "${rec.name}"?`)) return;
    await api.delete(`/invoicing/recurring/${rec.recurring_id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => nav("/invoicing/recurring/new")} data-testid="new-recurring-btn">
          <Plus className="h-4 w-4 mr-1" /> New recurring invoice
        </Button>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
                <th className="p-3">Name</th><th className="p-3">Client</th>
                <th className="p-3 text-right">Monthly total</th>
                <th className="p-3">Day</th><th className="p-3">Next run</th>
                <th className="p-3">Auto-send</th><th className="p-3">Active</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((rec) => (
                <tr key={rec.recurring_id} className="border-b"
                  data-testid={`recurring-row-${rec.name}`}>
                  <td className="p-3 font-medium">{rec.name}</td>
                  <td className="p-3">{rec.client_id}</td>
                  <td className="p-3 text-right">{fmtMoney(rec.total)}</td>
                  <td className="p-3">{rec.day_of_month}</td>
                  <td className="p-3">{rec.next_run_date}</td>
                  <td className="p-3">{rec.auto_send
                    ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Auto-send</Badge>
                    : <span className="text-zinc-500 text-xs">Draft for review</span>}</td>
                  <td className="p-3">
                    <Switch checked={!!rec.active} onCheckedChange={() => toggleActive(rec)}
                      data-testid={`recurring-active-toggle-${rec.recurring_id}`} />
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" title="Generate now"
                      disabled={running === rec.recurring_id}
                      data-testid={`recurring-run-now-${rec.recurring_id}`}
                      onClick={() => runNow(rec)}>
                      {running === rec.recurring_id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Play className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => nav(`/invoicing/recurring/${rec.recurring_id}/edit`)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(rec)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={8} className="p-8 text-center text-zinc-400">
                  No recurring invoices. Set up monthly packages, hosting, domains and SaaS fees here.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const EMPTY_ITEM = { name: "", description: "", unit_price: 0, gst: true, unit: "each", category: "" };

function ItemsTab() {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/invoicing/items");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...dialog, unit_price: Number(dialog.unit_price) };
      if (dialog.item_id) await api.put(`/invoicing/items/${dialog.item_id}`, body);
      else await api.post("/invoicing/items", body);
      setDialog(null); load();
      toast.success("Item saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await api.delete(`/invoicing/items/${it.item_id}`); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialog({ ...EMPTY_ITEM })} data-testid="new-item-btn">
          <Plus className="h-4 w-4 mr-1" /> New item
        </Button>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
                <th className="p-3">Name</th><th className="p-3">Category</th>
                <th className="p-3 text-right">Unit price (ex GST)</th>
                <th className="p-3">Unit</th><th className="p-3">GST</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.item_id} className="border-b" data-testid={`item-row-${it.item_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-zinc-400">{it.description}</div>
                  </td>
                  <td className="p-3">{it.category || "—"}</td>
                  <td className="p-3 text-right">{fmtMoney(it.unit_price)}</td>
                  <td className="p-3">{it.unit}</td>
                  <td className="p-3">{it.gst ? "10%" : "GST free"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setDialog({ ...it })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(it)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader><DialogTitle>{dialog?.item_id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <Input placeholder="Name" value={dialog.name} data-testid="item-name-input"
                onChange={(e) => setDialog({ ...dialog, name: e.target.value })} />
              <Input placeholder="Description" value={dialog.description || ""}
                onChange={(e) => setDialog({ ...dialog, description: e.target.value })} />
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" step="0.01" placeholder="Unit price"
                  value={dialog.unit_price} data-testid="item-price-input"
                  onChange={(e) => setDialog({ ...dialog, unit_price: e.target.value })} />
                <Input placeholder="Unit (hour/each/user)" value={dialog.unit}
                  onChange={(e) => setDialog({ ...dialog, unit: e.target.value })} />
                <Input placeholder="Category" value={dialog.category || ""}
                  onChange={(e) => setDialog({ ...dialog, category: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!dialog.gst}
                  onCheckedChange={(v) => setDialog({ ...dialog, gst: v })} />
                GST applies (10%)
              </label>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} data-testid="item-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/invoicing/settings"),
          api.get("/integrations/xero/tenants").catch(() => ({ data: [] })),
        ]);
        setS(a.data); setTenants(b.data || []);
      } catch (e) { toast.error("Failed to load settings"); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/invoicing/settings", {
        default_tenant_id: s.default_tenant_id || null,
        sales_account_code: s.sales_account_code || "200",
        payment_account_code: s.payment_account_code || null,
        bank_details: s.bank_details || null,
        entity_address: s.entity_address || null,
        footer_text: s.footer_text || null,
        default_due_days: Number(s.default_due_days ?? 14),
        commbank_merchant_id: s.commbank_merchant_id || null,
        commbank_api_key: s.commbank_api_key || null,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <Card><CardContent className="p-5 space-y-4 max-w-2xl">
      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
        Pushing invoices to Xero requires write scopes. If your Xero connection predates
        this module, open <Link to="/integrations/xero" className="underline font-medium">Xero (master)</Link> and
        click <b>Connect Xero</b> once to re-consent.
      </div>
      <F label="Default Xero organisation (invoices push here unless overridden per invoice)">
        <Select value={s.default_tenant_id || "none"}
          onValueChange={(v) => setS({ ...s, default_tenant_id: v === "none" ? null : v })}>
          <SelectTrigger data-testid="settings-default-tenant"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— none —</SelectItem>
            {tenants.map((t) => (
              <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </F>
      <div className="grid grid-cols-3 gap-3">
        <F label="Xero sales account code">
          <Input value={s.sales_account_code || ""} data-testid="settings-sales-code"
            onChange={(e) => setS({ ...s, sales_account_code: e.target.value })} />
        </F>
        <F label="Xero payment account code">
          <Input value={s.payment_account_code || ""} placeholder="e.g. 090"
            data-testid="settings-payment-code"
            onChange={(e) => setS({ ...s, payment_account_code: e.target.value })} />
        </F>
        <F label="Default due days">
          <Input type="number" value={s.default_due_days ?? 14}
            onChange={(e) => setS({ ...s, default_due_days: e.target.value })} />
        </F>
      </div>
      <F label="Direct deposit details (shown on invoice PDF)">
        <Textarea rows={3} value={s.bank_details || ""} data-testid="settings-bank-details"
          placeholder={"Bank: CBA\nBSB: 000-000\nAccount: 12345678"}
          onChange={(e) => setS({ ...s, bank_details: e.target.value })} />
      </F>
      <F label="Issuing entity address (PDF header)">
        <Textarea rows={2} value={s.entity_address || ""}
          onChange={(e) => setS({ ...s, entity_address: e.target.value })} />
      </F>
      <F label="Invoice footer text">
        <Textarea rows={2} value={s.footer_text || ""}
          onChange={(e) => setS({ ...s, footer_text: e.target.value })} />
      </F>
      <div className="border-t pt-4">
        <div className="text-sm font-semibold mb-1">Online card payments</div>
        <div className="text-sm mb-3">
          Stripe: {s.stripe_configured
            ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Configured</Badge>
            : <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Not configured</Badge>}
        </div>
        <div className="text-xs text-zinc-500 mb-2">
          CommBank online merchant facility (Simplify/CommWeb) — coming soon. Store your
          credentials now; they're encrypted at rest.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="CommBank merchant ID">
            <Input value={s.commbank_merchant_id || ""} data-testid="settings-commbank-merchant"
              onChange={(e) => setS({ ...s, commbank_merchant_id: e.target.value })} />
          </F>
          <F label={`CommBank API key ${s.commbank_api_key_set ? "(saved — enter to replace)" : ""}`}>
            <Input type="password" value={s.commbank_api_key || ""}
              onChange={(e) => setS({ ...s, commbank_api_key: e.target.value })} />
          </F>
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="settings-save-btn">
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save settings
      </Button>
    </CardContent></Card>
  );
}

export default function InvoicingPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "invoices";
  return (
    <div className="space-y-5" data-testid="invoicing-page">
      <div className="flex items-center gap-3">
        <BadgeDollarSign className="h-6 w-6 text-[#002FA7]" />
        <div>
          <h1 className="text-2xl font-bold">Invoicing</h1>
          <p className="text-sm text-zinc-500">
            Create, send and collect invoices — pushed to Xero when paid.
          </p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Products &amp; Services</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
        <TabsContent value="recurring"><RecurringTab /></TabsContent>
        <TabsContent value="items"><ItemsTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
